import { Box, Chip, Link, Typography, useTheme } from '@mui/material'
import { CustomContainer } from '../../components/Wrapper/CustomContainer'
import { GridBreakpoint } from '../../interfaces/IGridSettings'
import { useCallback, useContext, useEffect, useState } from 'react'
import { StripedDataGrid, StyledGridOverlay } from '../../components/DataGrid/StyledComponents'
import { nbNO } from '@mui/x-data-grid/locales'
import { GridCellModesModel, GridColDef, GridRowModel } from '@mui/x-data-grid'
import { blue, green, lightBlue } from '@mui/material/colors'
import { eventIdTextColor, toDateTime } from '../../utils/ValueFormatters'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'
import { EditButtonCell } from './WorkTableEditButtonCell'
import { WorkResponse } from '../../interfaces/IWork'
import { WorkStatus } from '../../interfaces/IWorkEnums'

export default function WorkDatagrid() {
  const theme = useTheme()
  const { context } = useContext(EbAuthenticatedAppContext)
  const [loading, setLoading] = useState(true)
  const [work, setWork] = useState<WorkResponse>({ entities: [], continuationToken: null })

  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({})

  const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel)
  }, [])

  const assignReferencesToWork = async (workId: number, assignmentId: string, externalReferenceId: string) => {
    setLoading(true)

    const buildRequest = context.restApiClient.buildRequest(`/api/work`)

    buildRequest.addUriParameter('workId', workId.toString())

    if (assignmentId) buildRequest.addUriParameter('assignmentId', assignmentId)

    if (externalReferenceId) buildRequest.addUriParameter('externalReferenceId', externalReferenceId)

    const response = await buildRequest.putAsync()

    setLoading(false)

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke oppdatere arbeid. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })

      throw new Error('Could not update work item')
    }

    return response
  }

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const processRowUpdate = useCallback(async (newRow: GridRowModel, oldRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false }
    try {
      await assignReferencesToWork(newRow.id as number, newRow.assignmentId as string, newRow.externalReferenceId as string)
    } catch (error) {
      console.error(error)
      return oldRow
    }

    return updatedRow
  }, [])

  const getWork = async () => {
    setLoading(true)

    const workResponse = await context.restApiClient.buildRequest('/api/work').deserializeResponseToJson<WorkResponse>().getAsync()

    if (workResponse.json) {
      setWork(workResponse.json)
    }

    if (workResponse.error) {
      setWork({ entities: [], continuationToken: null })
      enqueueSnackbar(<Typography variant="body2">Kunne ikke hente arbeid. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }

    return setLoading(false)
  }

  const workListStatusChip = (status: string) => {
    if (status === 'InProgress') {
      return <Chip sx={{ bgcolor: blue[200], color: 'black' }} label={WorkStatus.InProgress} />
    }
    if (status === 'Completed') {
      return <Chip sx={{ bgcolor: green[300], color: 'black' }} label={WorkStatus.Completed} />
    }
    return <Chip sx={{ bgcolor: 'grey.500', color: 'black' }} label={WorkStatus.Unknown} />
  }

  const statusLabelMapper = (status: string) => {
    if (status === 'InProgress') return WorkStatus.InProgress
    if (status === 'Completed') return WorkStatus.Completed
    if (status === 'Unknown') return WorkStatus.Unknown
    return 'Alle'
  }

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      type: 'singleSelect',
      getOptionValue: (value: any) => value || '',
      getOptionLabel: (value: any) => statusLabelMapper(value),
      valueOptions: ['InProgress', 'Completed', 'Unknown'],
      valueGetter: (value, row, column, apiRef) => row.status ?? WorkStatus.Unknown,
      renderCell: (params) => {
        return workListStatusChip(params.value as WorkStatus)
      },
    },

    { field: 'message', headerName: 'Arbeidsmelding', minWidth: 300, flex: 1 },
    { field: 'assetLocation', headerName: 'Anlegg/Lokasjon', minWidth: 220, width: 220 },
    {
      field: 'workerPhoneNumber',
      headerName: 'Arbeider - Tlf',
      minWidth: 130,
      width: 130,
      renderCell: (params) => {
        return <Link href={`tel:${params.value}`}>{params.value}</Link>
      },
    },
    {
      field: 'assignmentId',
      headerName: 'Oppdrag',
      minWidth: 110,
      width: 110,
      editable: true,
      description: 'Knyttet til oppdrag i Oppdragsportalen',
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <EditButtonCell {...params} setCellModesModel={setCellModesModel} />
            </Box>
          );
        }
        if (params.value) {
          return (
            <a
              href={`https://oppdrag.digital.eidsiva.no/assignment/${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                fontWeight: 'bold',
                color: lightBlue[700],
              }}
            >
              {params.value}
            </a>
          )
        }
      },
    },
    {
      field: 'externalReferenceId',
      headerName: 'Hendelse',
      minWidth: 110,
      width: 110,
      editable: true,
      description: 'Knyttet til hendelse i Eventlog',
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <EditButtonCell {...params} setCellModesModel={setCellModesModel} />
            </Box>
          );
        }
        if (params.value) {
          return (
            <a
              href={`https://eventlog.internal.digital.eidsiva.no/events/${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                fontWeight: 'bold',
                color: eventIdTextColor(params.value, theme),
              }}
            >
              {params.value}
            </a>
          )
        }
      },
    },
    {
      field: 'startedDate',
      headerName: 'Startet',
      minWidth: 130,
      width: 130,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => (value ? new Date(value as Date) : null),
    },
    {
      field: 'completedDate',
      headerName: 'Fullført',
      minWidth: 130,
      width: 130,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => (value ? new Date(value as Date) : null),
    },
  ]

  useEffect(() => {
    getWork()
  }, [])

  return (
    <CustomContainer gridWidth={GridBreakpoint.xxl}>
      <StripedDataGrid
        label="Arbeidslogg"
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        loading={loading}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
        }}
        rows={work.entities ?? []}
        columns={columns}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        processRowUpdate={processRowUpdate}
        slots={{
          noRowsOverlay: () => <StyledGridOverlay>Ingen arbeid pågår</StyledGridOverlay>,
        }}
        showToolbar={true}
        showCellVerticalBorder={true}
        showColumnVerticalBorder={true}
        hideFooterSelectedRowCount={true}
        sortModel={[{ field: 'startedDate', sort: 'desc' }]}
        rowHeight={45}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        columnHeaderHeight={45}
      />
    </CustomContainer>
  )
}
